import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  BookOpen,
  FolderOpen,
  FileText,
  Loader2,
  RefreshCw,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Circle,
  Repeat,
  XCircle,
  Link2,
  Unlink,
  Copy,
  Check,
  ExternalLink,
  Clock,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import {
  api,
  type Notebook,
  type Section,
  type Page,
  type SyncScope,
  type AutoSyncRule,
  type PageStatus,
} from "../lib/api.js";
import { Button } from "./ui/button.js";
import { Input } from "./ui/forms.js";
import { cn } from "../lib/cn.js";
import { useWsFrames } from "../lib/ws-client.js";

type SyncProgressData = Awaited<ReturnType<typeof api.jobProgress>>;

const DEFAULT_HOSTNAME = "dongandpartners.sharepoint.com";
const INTERVAL_OPTIONS = [
  { label: "5 min", value: 5 },
  { label: "15 min", value: 15 },
  { label: "1 hr", value: 60 },
  { label: "6 hr", value: 360 },
  { label: "24 hr", value: 1440 },
];

interface CorpusBrowserProps {
  /** Set of `${scopeType}:${scopeId}` keys with running jobs (from SSE). */
  runningScopeKeys: Set<string>;
  /** Map of pageId → jobId for pages currently being processed (from SSE). */
  livePages: Record<string, number>;
  /** Jobs that were already running on the server when the tab loaded —
   *  emitted by useSyncActivity's reseed. Used to re-mount
   *  SyncProgressBanner after a page refresh. */
  rehydratedJobIds?: string[];
  /** Lifted page-selection callback so App can render PageDetailPanel. */
  onPageSelected: (page: Page & { sectionId: string; sectionName: string; notebookId: string; notebookName: string; siteId: string }) => void;
  selectedPageId: string | null;
}

export function CorpusBrowser({
  runningScopeKeys,
  livePages,
  rehydratedJobIds,
  onPageSelected,
  selectedPageId,
}: CorpusBrowserProps) {
  const [hostname, setHostname] = useState(DEFAULT_HOSTNAME);
  const [siteId, setSiteId] = useState<string | null>(null);
  const [notebook, setNotebook] = useState<Notebook | null>(null);
  const [section, setSection] = useState<Section | null>(null);

  const qc = useQueryClient();

  const msStatusQ = useQuery({
    queryKey: ["microsoftStatus"],
    queryFn: () => api.microsoftStatus(),
    refetchInterval: 30_000,
  });

  const msDisconnect = useMutation({
    mutationFn: api.microsoftDisconnect,
    onSuccess: () => void msStatusQ.refetch(),
  });

  // Per-user Azure app registration config. source="unset" blocks the
  // Connect button until the user pastes their own clientId + tenantId.
  const msConfigQ = useQuery({
    queryKey: ["microsoftConfig"],
    queryFn: () => api.microsoftGetConfig(),
  });
  const msConfigSet = useMutation({
    mutationFn: ({ clientId, tenantId }: { clientId: string; tenantId: string }) =>
      api.microsoftSetConfig(clientId, tenantId),
    onSuccess: () => void msConfigQ.refetch(),
  });
  const msConfigClear = useMutation({
    mutationFn: () => api.microsoftClearConfig(),
    onSuccess: () => void msConfigQ.refetch(),
  });
  const [configClientId, setConfigClientId] = useState("");
  const [configTenantId, setConfigTenantId] = useState("");
  const [editingConfig, setEditingConfig] = useState(false);
  const [configHelpOpen, setConfigHelpOpen] = useState(false);

  const [deviceCodeState, setDeviceCodeState] = useState<{
    userCode: string;
    verificationUri: string;
    interval: number;
    expiresAt: number;
  } | null>(null);
  const [msPolling, setMsPolling] = useState(false);
  const [msCopied, setMsCopied] = useState(false);
  const [msExpired, setMsExpired] = useState(false);
  const [msError, setMsError] = useState<string | null>(null);
  const [msPollTick, setMsPollTick] = useState(0);
  const msPollErrors = useRef(0);

  const configReady =
    (msConfigQ.data?.source ?? "unset") !== "unset";
  const showConfigForm = !configReady || editingConfig;

  const handleSaveConfig = () => {
    const c = configClientId.trim();
    const t = configTenantId.trim();
    const guidRe = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!guidRe.test(c) || !guidRe.test(t)) {
      setMsError("Client ID and Tenant ID must be GUIDs (8-4-4-4-12 hex).");
      return;
    }
    setMsError(null);
    msConfigSet.mutate(
      { clientId: c, tenantId: t },
      {
        onSuccess: () => {
          setEditingConfig(false);
          setConfigClientId("");
          setConfigTenantId("");
        },
        onError: (err) =>
          setMsError(err instanceof Error ? err.message : "Save failed"),
      },
    );
  };

  const handleStartDeviceCode = async () => {
    try {
      setMsError(null);
      setMsExpired(false);
      msPollErrors.current = 0;
      const result = await api.microsoftStartDeviceCode();
      setDeviceCodeState({
        userCode: result.userCode,
        verificationUri: result.verificationUri,
        interval: result.interval,
        expiresAt: Date.now() + result.expiresIn * 1000,
      });
      setMsPollTick(0);
      setMsPolling(true);
    } catch (err) {
      setMsError(err instanceof Error ? err.message : "Failed to start device code flow");
    }
  };

  useEffect(() => {
    if (!msPolling || !deviceCodeState) return;
    if (Date.now() > deviceCodeState.expiresAt) {
      setMsPolling(false);
      setMsExpired(true);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const result = await api.microsoftPollDeviceCode();
        msPollErrors.current = 0;
        if (result.connected) {
          setMsPolling(false);
          setDeviceCodeState(null);
          void msStatusQ.refetch();
        } else if (result.expired) {
          setMsPolling(false);
          setMsExpired(true);
          setDeviceCodeState(null);
        } else {
          setMsPollTick((t) => t + 1);
        }
      } catch {
        msPollErrors.current += 1;
        if (msPollErrors.current >= 3) {
          setMsPolling(false);
          setMsError("Polling failed. Please try again.");
          setDeviceCodeState(null);
          msPollErrors.current = 0;
        } else {
          setMsPollTick((t) => t + 1);
        }
      }
    }, deviceCodeState.interval * 1000);
    return () => clearTimeout(timer);
  }, [msPolling, deviceCodeState, msPollTick]);

  const handleCopyCode = () => {
    if (deviceCodeState) {
      navigator.clipboard.writeText(deviceCodeState.userCode);
      setMsCopied(true);
      setTimeout(() => setMsCopied(false), 2000);
    }
  };

  const connectSite = useMutation({
    mutationFn: (h: string) => api.getSite(h),
    onSuccess: (site) => {
      setSiteId(site.id);
      setNotebook(null);
      setSection(null);
    },
    onError: (err) =>
      alert(`Failed to connect: ${err instanceof Error ? err.message : err}`),
  });

  const handleConnect = () => {
    if (!msStatusQ.data?.connected) {
      setMsError("Please connect your Microsoft account first.");
      return;
    }
    connectSite.mutate(hostname);
  };

  const notebooksQ = useQuery({
    queryKey: ["notebooks", siteId],
    queryFn: () => api.listNotebooks(siteId!),
    enabled: !!siteId,
  });

  const sectionsQ = useQuery({
    queryKey: ["sections", siteId, notebook?.id],
    queryFn: () => api.listSections(siteId!, notebook!.id),
    enabled: !!siteId && !!notebook,
  });

  // Pages for the selected section. Server returns one Graph batch
  // (~100 pages) per call; we hold the cursor in state and fetch the
  // next batch when the user scrolls near the end of the list. No
  // cache.
  const [pages, setPages] = useState<Page[]>([]);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [pagesError, setPagesError] = useState<string | null>(null);
  const [pagesNextLink, setPagesNextLink] = useState<string | null>(null);
  const [pagesFetchingMore, setPagesFetchingMore] = useState(false);

  useWsFrames((frame) => {
    if (frame.kind === "sync_progress") {
      const progress = frame.progress as SyncProgressData | undefined;
      if (!progress) return;
      const jobId = String(frame.jobId ?? progress.jobId);
      if (cleanupTimersRef.current[jobId]) {
        clearTimeout(cleanupTimersRef.current[jobId]);
        delete cleanupTimersRef.current[jobId];
      }
      setJobProgressMap((prev) => ({ ...prev, [jobId]: progress }));
      if (progress.status === "completed" || progress.status === "failed") {
        cleanupTimersRef.current[jobId] = setTimeout(() => {
          setJobProgressMap((prev) => {
            const next = { ...prev };
            delete next[jobId];
            return next;
          });
          delete cleanupTimersRef.current[jobId];
        }, 5000);
      }
      return;
    }
    if (frame.kind !== "cache_update") return;
    const key = String(frame.cacheKey ?? "");
    if (key.startsWith("notebooks#")) {
      void qc.invalidateQueries({ queryKey: ["notebooks"] });
    } else if (key.startsWith("sections#")) {
      void qc.invalidateQueries({ queryKey: ["sections"] });
    }
  });

  useEffect(() => {
    const timers = cleanupTimersRef.current;
    return () => {
      for (const t of Object.values(timers)) clearTimeout(t);
    };
  }, []);

  // Section-request token: bumped on every section change so in-flight
  // loadMorePages calls from a previous section can detect they're stale
  // and skip their setState.
  const sectionRequestIdRef = useRef(0);

  // First batch when section changes.
  useEffect(() => {
    sectionRequestIdRef.current += 1;
    const requestId = sectionRequestIdRef.current;
    if (!siteId || !section) {
      setPages([]);
      setPagesNextLink(null);
      setPagesError(null);
      return;
    }
    setPages([]);
    setPagesNextLink(null);
    setPagesLoading(true);
    setPagesError(null);
    api
      .listPages(siteId, section.id)
      .then((res) => {
        if (sectionRequestIdRef.current !== requestId) return;
        setPages(res.pages);
        setPagesNextLink(res.nextLink);
      })
      .catch((err) => {
        if (sectionRequestIdRef.current !== requestId) return;
        setPagesError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (sectionRequestIdRef.current !== requestId) return;
        setPagesLoading(false);
      });
  }, [siteId, section?.id]);

  // Scroll-triggered load-more. VirtualizedPageList calls this when the
  // user scrolls within a few rows of the end. Guard against overlapping
  // fetches via `pagesFetchingMore`.
  const loadMorePages = useCallback(() => {
    if (!siteId || !section || !pagesNextLink || pagesFetchingMore) return;
    const cursor = pagesNextLink;
    const sectionId = section.id;
    const requestId = sectionRequestIdRef.current;
    setPagesFetchingMore(true);
    api
      .listPages(siteId, sectionId, cursor)
      .then((res) => {
        if (sectionRequestIdRef.current !== requestId) return;
        setPages((prev) => [...prev, ...res.pages]);
        setPagesNextLink(res.nextLink);
      })
      .catch((err) => {
        if (sectionRequestIdRef.current !== requestId) return;
        setPagesError(err instanceof Error ? err.message : String(err));
        setPagesNextLink(null);
      })
      .finally(() => {
        if (sectionRequestIdRef.current !== requestId) return;
        setPagesFetchingMore(false);
      });
  }, [siteId, section?.id, pagesNextLink, pagesFetchingMore]);

  // After each batch settles, auto-load the next one after 500 ms so the
  // list fills in without the user needing to scroll. Stops when
  // nextLink is null (section fully loaded) or the section changes.
  useEffect(() => {
    if (!pagesNextLink || pagesFetchingMore) return;
    const timer = setTimeout(() => loadMorePages(), 500);
    return () => clearTimeout(timer);
  }, [pagesNextLink, pagesFetchingMore, loadMorePages]);

  const corpusStatusQ = useQuery({
    queryKey: ["corpusStatus", siteId, notebook?.id, section?.id],
    queryFn: () =>
      api.corpusStatus({
        siteId: siteId ?? undefined,
        notebookId: notebook?.id,
        sectionId: section?.id,
      }),
    enabled: !!siteId,
    refetchInterval: 10_000,
  });

  const autoSyncQ = useQuery({
    queryKey: ["autoSync"],
    queryFn: () => api.listAutoSync(),
    refetchInterval: 30_000,
  });

  const statusByPage = useMemo(() => {
    const map: Record<string, PageStatus> = {};
    for (const p of corpusStatusQ.data?.pages ?? []) map[p.pageId] = p;
    return map;
  }, [corpusStatusQ.data]);

  const autoByScope = useMemo(() => {
    const map: Record<string, AutoSyncRule> = {};
    for (const r of autoSyncQ.data?.rules ?? []) {
      map[`${r.scopeType}:${r.scopeId}`] = r;
    }
    return map;
  }, [autoSyncQ.data]);

  const [activeJobIds, setActiveJobIds] = useState<string[]>([]);
  const dismissedJobIdsRef = useRef<Set<string>>(new Set());
  const [jobProgressMap, setJobProgressMap] = useState<Record<string, SyncProgressData>>({});
  const cleanupTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const activeSyncsByScope = useMemo(() => {
    const map: Record<string, SyncProgressData> = {};
    for (const p of Object.values(jobProgressMap)) {
      if (p.status === "queued" || p.status === "running" || p.status === "rate_limited") {
        map[`${p.scopeType}:${p.scopeId}`] = p;
      }
    }
    return map;
  }, [jobProgressMap]);

  // When useSyncActivity discovers in-flight jobs on the server (on
  // mount or after a reconnect), merge them into activeJobIds so the
  // SyncProgressBanner renders for each. Jobs drop out via handleJobDone
  // as they complete.
  useEffect(() => {
    if (!rehydratedJobIds || rehydratedJobIds.length === 0) return;
    const fresh = rehydratedJobIds.filter((id) => !dismissedJobIdsRef.current.has(id));
    if (fresh.length === 0) return;
    setActiveJobIds((prev) => {
      const seen = new Set(prev);
      const merged = [...prev];
      for (const id of fresh) {
        if (!seen.has(id)) merged.push(id);
      }
      return merged.length === prev.length ? prev : merged;
    });
    for (const id of fresh) {
      if (!jobProgressMap[id]) {
        void api.jobProgress(id).then((p) => {
          setJobProgressMap((prev) => ({ ...prev, [id]: p }));
        });
      }
    }
  }, [rehydratedJobIds]);

  // Optimistic "running" state — populated the moment the user clicks
  // Sync, before the HTTP request returns and before the first WS
  // progress frame arrives. Prevents the button from looking inert
  // during the 200-500 ms handshake. Gets unioned with
  // `runningScopeKeys` (authoritative via WS/rehydrate) when rendering.
  const [optimisticRunningKeys, setOptimisticRunningKeys] = useState<Set<string>>(new Set());

  // Single sync mutation. The "force" axis was retired with the three-tier
  // refactor — the lazy decision tree in the raw-fetch worker makes the old
  // "lazy sync" button pointless (it always cache-hits when nothing's
  // changed) and the staging cache makes "force re-extract" cheap on
  // unchanged content. One button, one path.
  const syncMut = useMutation({
    mutationFn: ({ scope }: { scope: SyncScope }) => api.sync(scope),
    onMutate: ({ scope }) => {
      const key = `${scope.type}:${scope.id}`;
      setOptimisticRunningKeys((prev) => {
        const next = new Set(prev);
        next.add(key);
        return next;
      });
      return { key };
    },
    onSuccess: (result) => {
      setActiveJobIds((prev) =>
        prev.includes(result.jobId) ? prev : [...prev, result.jobId],
      );
      void api.jobProgress(result.jobId).then((p) => {
        setJobProgressMap((prev) => ({ ...prev, [result.jobId]: p }));
      });
      void qc.invalidateQueries({ queryKey: ["jobs"] });
      void corpusStatusQ.refetch();
    },
    onError: (err) => {
      // Don't alert — the most common failure is a 409 conflict (a sync
      // is already running for the scope), and the SPA banner shows that
      // job. Anything genuinely broken lands in console for diagnostics.
      console.error("scope sync failed:", err);
    },
    onSettled: (_data, _err, _vars, ctx) => {
      // Clear the optimistic flag. By now either (a) runningScopeKeys
      // already has the key from the WS job_started frame, or (b) the
      // request failed and we want the button active again.
      if (ctx && typeof ctx === "object" && "key" in ctx) {
        const key = (ctx as { key: string }).key;
        setOptimisticRunningKeys((prev) => {
          if (!prev.has(key)) return prev;
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
  });

  const isScopeRunning = useCallback(
    (scopeType: string, scopeId: string) => {
      const key = `${scopeType}:${scopeId}`;
      return runningScopeKeys.has(key) || optimisticRunningKeys.has(key) || key in activeSyncsByScope;
    },
    [runningScopeKeys, optimisticRunningKeys, activeSyncsByScope],
  );

  const handleJobDone = useCallback(
    (jobId: string) => {
      dismissedJobIdsRef.current.add(jobId);
      setActiveJobIds((prev) => prev.filter((id) => id !== jobId));
      void corpusStatusQ.refetch();
    },
    [corpusStatusQ],
  );

  const setAuto = useMutation({
    mutationFn: api.setAutoSync,
    onSuccess: () => void autoSyncQ.refetch(),
  });

  // Refetch corpus status whenever a job ends (live page count changes).
  // We rely on App's shared SSE state — when the runningKeys / livePages
  // change shape we re-read corpus status.
  useEffect(() => {
    void corpusStatusQ.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runningScopeKeys.size, Object.keys(livePages).length]);

  return (
    <div className="space-y-3">
      {activeJobIds.length > 0 && (
        <div className="space-y-1">
          {activeJobIds.map((id) => (
            <SyncProgressBanner key={id} jobId={id} onDone={handleJobDone} />
          ))}
        </div>
      )}
      {!msStatusQ.data || !msConfigQ.data ? (
        // Initial mount: don't flash the empty "paste your client ID" form
        // while /api/auth/microsoft/{status,config} are still round-tripping.
        // Once either call resolves the normal branches take over.
        <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs text-slate-500">
          <Loader2 className="h-3 w-3 animate-spin" />
          Checking credentials, please wait…
        </div>
      ) : !msStatusQ.data?.connected ? (
        <div className="rounded-md border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
            <Link2 className="h-4 w-4" />
            Microsoft Setup
          </div>
          {showConfigForm ? (
            <div className="mt-2 space-y-2">
              <p className="text-xs text-slate-500">
                Paste the{" "}
                <strong>Application (client) ID</strong> and{" "}
                <strong>Directory (tenant) ID</strong> from your Microsoft
                Entra app registration.
              </p>
              <Input
                value={configClientId}
                onChange={(e) => setConfigClientId(e.target.value)}
                placeholder="Client ID (e.g. c4622a00-...)"
              />
              <Input
                value={configTenantId}
                onChange={(e) => setConfigTenantId(e.target.value)}
                placeholder="Tenant ID (e.g. fd3e3b36-...)"
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleSaveConfig}
                  disabled={msConfigSet.isPending}
                >
                  {msConfigSet.isPending ? (
                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                  ) : null}
                  Save
                </Button>
                {editingConfig && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setEditingConfig(false);
                      setConfigClientId("");
                      setConfigTenantId("");
                      setMsError(null);
                    }}
                  >
                    Cancel
                  </Button>
                )}
              </div>
              <button
                onClick={() => setConfigHelpOpen((o) => !o)}
                className="text-xs text-slate-500 underline hover:text-slate-700"
              >
                {configHelpOpen ? "Hide" : "How do I get these?"}
              </button>
              {configHelpOpen && (
                <ol className="list-inside list-decimal space-y-1 rounded-md bg-slate-50 p-3 text-xs text-slate-600">
                  <li>
                    Sign in to{" "}
                    <a
                      href="https://entra.microsoft.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline"
                    >
                      entra.microsoft.com
                    </a>{" "}
                    as an Application Administrator.
                  </li>
                  <li>
                    Applications → App registrations → + New registration.
                  </li>
                  <li>
                    Single-tenant; leave Redirect URI blank.
                  </li>
                  <li>
                    Authentication → Advanced → <strong>Allow public client flows = Yes</strong> → Save.
                  </li>
                  <li>
                    API permissions → Add (delegated on Microsoft Graph):{" "}
                    <code>Notes.Read</code>, <code>Notes.Read.All</code>,{" "}
                    <code>Sites.Read.All</code>, <code>Files.Read.All</code>,{" "}
                    <code>offline_access</code> → Grant admin consent.
                  </li>
                  <li>
                    Overview → copy the <em>Application (client) ID</em>{" "}
                    and <em>Directory (tenant) ID</em> into the fields above.
                  </li>
                </ol>
              )}
            </div>
          ) : (
            <div className="mt-2 space-y-2">
              <p className="text-xs text-slate-500">
                Using Azure app{" "}
                <code className="rounded bg-slate-100 px-1 text-slate-700">
                  {(msConfigQ.data?.clientId ?? "").slice(0, 8)}…
                </code>{" "}
                in tenant{" "}
                <code className="rounded bg-slate-100 px-1 text-slate-700">
                  {(msConfigQ.data?.tenantId ?? "").slice(0, 8)}…
                </code>{" "}
                <span className="text-slate-400">
                  ({msConfigQ.data?.source})
                </span>
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setEditingConfig(true);
                    setConfigClientId(msConfigQ.data?.clientId ?? "");
                    setConfigTenantId(msConfigQ.data?.tenantId ?? "");
                  }}
                  className="text-xs text-slate-600 underline hover:text-slate-900"
                >
                  Edit
                </button>
                {msConfigQ.data?.source === "user" && (
                  <button
                    onClick={() => msConfigClear.mutate()}
                    className="text-xs text-slate-500 underline hover:text-slate-700"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="my-3 border-t border-slate-100" />

          <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
            <Link2 className="h-4 w-4" />
            Connect Microsoft Account
          </div>
          <p className="mt-1 text-xs text-slate-500">
            Link your Microsoft account to browse OneNote notebooks and SharePoint sites.
          </p>
          {msError && (
            <p className="mt-2 text-xs text-red-600">{msError}</p>
          )}
          {msExpired && (
            <p className="mt-2 text-xs text-amber-600">
              Device code expired. Please try again.
            </p>
          )}
          {!deviceCodeState && !msPolling && (
            <Button
              size="sm"
              className="mt-3"
              onClick={handleStartDeviceCode}
              disabled={msStatusQ.isLoading || !configReady}
              title={!configReady ? "Enter your Azure Client ID and Tenant ID first" : undefined}
            >
              {msStatusQ.isLoading ? (
                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
              ) : (
                <Link2 className="mr-1 h-3 w-3" />
              )}
              Connect Microsoft Account
            </Button>
          )}
          {deviceCodeState && msPolling && (
            <div className="mt-3 space-y-3">
              <p className="text-xs text-slate-600">
                To link your Microsoft account:
              </p>
              <ol className="list-inside list-decimal space-y-1.5 text-xs text-slate-700">
                <li>
                  Open{" "}
                  <a
                    href={deviceCodeState.verificationUri}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-blue-600 underline hover:text-blue-800"
                  >
                    {deviceCodeState.verificationUri}
                    <ExternalLink className="ml-0.5 inline h-3 w-3" />
                  </a>
                </li>
                <li>Enter the code below when prompted</li>
                <li>Authorize the application</li>
              </ol>
              <div className="flex items-center gap-2">
                <code className="rounded-md border border-slate-300 bg-slate-50 px-4 py-2 text-lg font-mono font-bold tracking-widest text-slate-900 select-all">
                  {deviceCodeState.userCode}
                </code>
                <button
                  onClick={handleCopyCode}
                  className="rounded-md border border-slate-200 p-2 text-slate-500 hover:bg-slate-50 hover:text-slate-700"
                  title="Copy code"
                >
                  {msCopied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Loader2 className="h-3 w-3 animate-spin" />
                <Clock className="h-3 w-3" />
                Waiting for authorization...
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
          <CheckCircle2 className="h-3 w-3" />
          Microsoft account connected
          <button
            className="ml-auto flex items-center gap-1 text-emerald-600 hover:text-emerald-800"
            onClick={() => msDisconnect.mutate()}
            disabled={msDisconnect.isPending}
            title="Disconnect Microsoft account"
          >
            <Unlink className="h-3 w-3" />
            Disconnect
          </button>
        </div>
      )}

      <div className="flex gap-2">
        <Input
          value={hostname}
          onChange={(e) => setHostname(e.target.value)}
          placeholder="SharePoint hostname"
          onKeyDown={(e) =>
            e.key === "Enter" &&
            !connectSite.isPending &&
            !!msStatusQ.data &&
            !!msConfigQ.data &&
            handleConnect()
          }
          disabled={connectSite.isPending || !msStatusQ.data || !msConfigQ.data}
        />
        <Button
          onClick={handleConnect}
          disabled={connectSite.isPending || !msStatusQ.data || !msConfigQ.data}
        >
          {connectSite.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Connecting…
            </>
          ) : (
            "Connect"
          )}
        </Button>
      </div>

      {siteId && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {/* Notebooks */}
          <Column title="Notebooks" icon={<BookOpen className="h-4 w-4" />}>
            {notebooksQ.isLoading ? (
              <LoadingRow />
            ) : notebooksQ.isError ? (
              <ErrorRow message={notebooksQ.error?.message ?? "error"} />
            ) : (
              notebooksQ.data?.notebooks.map((nb) => {
                const auto = autoByScope[`notebook:${nb.id}`];
                const running = isScopeRunning("notebook", nb.id);
                const nbProgress = activeSyncsByScope[`notebook:${nb.id}`];
                return (
                  <Row
                    key={nb.id}
                    label={nb.displayName}
                    selected={notebook?.id === nb.id}
                    onClick={() => {
                      setNotebook(nb);
                      setSection(null);
                    }}
                    badge={
                      auto?.enabled ? <AutoBadge rule={auto} /> : undefined
                    }
                    actions={
                      <>
                        <ScopeActions
                          scope={{
                            type: "notebook",
                            id: nb.id,
                            siteId,
                            label: nb.displayName,
                          }}
                          autoRule={auto}
                          onSync={() =>
                            syncMut.mutate({
                              scope: {
                                type: "notebook",
                                id: nb.id,
                                siteId,
                                label: nb.displayName,
                              },
                            })
                          }
                          onSetAuto={(rule) =>
                            setAuto.mutate({
                              scopeType: "notebook",
                              scopeId: nb.id,
                              scopeLabel: nb.displayName,
                              ...rule,
                            })
                          }
                          running={running}
                        />
                        {nbProgress && <ScopeSyncProgress progress={nbProgress} />}
                      </>
                    }
                  />
                );
              })
            )}
          </Column>

          {/* Sections */}
          <Column title="Sections" icon={<FolderOpen className="h-4 w-4" />}>
            {!notebook ? (
              <Hint>Select a notebook</Hint>
            ) : sectionsQ.isLoading ? (
              <LoadingRow />
            ) : sectionsQ.isError ? (
              <ErrorRow message={sectionsQ.error?.message ?? "error"} />
            ) : (
              sectionsQ.data?.sections.map((sec) => {
                const auto = autoByScope[`section:${sec.id}`];
                const running = isScopeRunning("section", sec.id);
                const secProgress = activeSyncsByScope[`section:${sec.id}`];
                return (
                  <Row
                    key={sec.id}
                    label={sec.displayName}
                    selected={section?.id === sec.id}
                    onClick={() => setSection(sec)}
                    badge={auto?.enabled ? <AutoBadge rule={auto} /> : undefined}
                    actions={
                      <>
                        <ScopeActions
                          scope={{
                            type: "section",
                            id: sec.id,
                            siteId,
                            notebookId: notebook.id,
                            notebookName: notebook.displayName,
                            label: `${notebook.displayName} / ${sec.displayName}`,
                          }}
                          autoRule={auto}
                          onSync={() =>
                            syncMut.mutate({
                              scope: {
                                type: "section",
                                id: sec.id,
                                siteId,
                                notebookId: notebook.id,
                                notebookName: notebook.displayName,
                                label: `${notebook.displayName} / ${sec.displayName}`,
                              },
                            })
                          }
                          onSetAuto={(rule) =>
                            setAuto.mutate({
                              scopeType: "section",
                              scopeId: sec.id,
                              scopeLabel: `${notebook.displayName} / ${sec.displayName}`,
                              ...rule,
                            })
                          }
                          running={running}
                        />
                        {secProgress && <ScopeSyncProgress progress={secProgress} />}
                      </>
                    }
                  />
                );
              })
            )}
          </Column>

          {/* Pages */}
          <Column
            title={section ? `Pages (${pages.length})` : "Pages"}
            icon={<FileText className="h-4 w-4" />}
          >
            {!section ? (
              <Hint>Select a section</Hint>
            ) : pagesLoading ? (
              <LoadingRow />
            ) : pagesError ? (
              <ErrorRow message={pagesError} />
            ) : pages.length > 0 ? (
              <VirtualizedPageList
                pages={pages}
                statusByPage={statusByPage}
                runningScopeKeys={runningScopeKeys}
                optimisticRunningKeys={optimisticRunningKeys}
                livePages={livePages}
                selectedPageId={selectedPageId}
                siteId={siteId!}
                notebook={notebook!}
                section={section}
                onPageSelected={onPageSelected}
                onStartSync={(scope) => syncMut.mutate({ scope })}
                hasMore={!!pagesNextLink}
                fetchingMore={pagesFetchingMore}
                onLoadMore={loadMorePages}
              />
            ) : (
              <Hint>No pages in this section</Hint>
            )}
          </Column>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Helpers
// ============================================================

function Column({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-md border border-slate-200 bg-white">
      <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs font-medium text-slate-600">
        {icon}
        {title}
      </div>
      <div className="max-h-96 overflow-y-auto">{children}</div>
    </div>
  );
}

function Row({
  label,
  selected,
  onClick,
  badge,
  actions,
}: {
  label: string;
  selected?: boolean;
  onClick?: () => void;
  badge?: React.ReactNode;
  actions?: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "border-b border-slate-100 last:border-b-0 hover:bg-slate-100",
        selected && "bg-slate-50",
      )}
    >
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          onClick={onClick}
          disabled={!onClick}
          className="flex flex-1 items-center gap-2 truncate text-left text-sm hover:underline disabled:cursor-default disabled:hover:no-underline"
        >
          <span className="truncate">{label}</span>
        </button>
        {badge}
      </div>
       {actions && <div className="px-3 pb-2">{actions}</div>}
    </div>
  );
}

function VirtualizedPageList({
  pages,
  statusByPage,
  runningScopeKeys,
  optimisticRunningKeys,
  livePages,
  selectedPageId,
  siteId,
  notebook,
  section,
  onPageSelected,
  onStartSync,
  hasMore,
  fetchingMore,
  onLoadMore,
}: {
  pages: Page[];
  statusByPage: Record<string, PageStatus>;
  runningScopeKeys: Set<string>;
  optimisticRunningKeys: Set<string>;
  livePages: Record<string, number>;
  selectedPageId: string | null;
  siteId: string;
  notebook: Notebook;
  section: Section;
  onPageSelected: CorpusBrowserProps["onPageSelected"];
  onStartSync: (scope: SyncScope) => void;
  hasMore: boolean;
  fetchingMore: boolean;
  onLoadMore: () => void;
}) {
  const parentRef = useRef<HTMLDivElement>(null);

  // Sentinel row: when hasMore, render one extra virtualized item past
  // the real pages. Gives the user a visible "Loading more…" indicator
  // AND makes the virtualizer's lastIdx reliably reach pages.length once
  // scrolled to the bottom, which triggers the fetch.
  const itemCount = pages.length + (hasMore ? 1 : 0);

  const virtualizer = useVirtualizer({
    count: itemCount,
    getScrollElement: () => parentRef.current,
    estimateSize: (i) => (i === pages.length ? 36 : 60),
    overscan: 5,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const lastIdx = virtualItems.length
    ? virtualItems[virtualItems.length - 1].index
    : 0;
  useEffect(() => {
    if (hasMore && !fetchingMore && lastIdx >= pages.length - 5) {
      onLoadMore();
    }
  }, [lastIdx, hasMore, fetchingMore, onLoadMore, pages.length]);

  return (
    <div ref={parentRef} className="max-h-96 overflow-y-auto">
      <div
        style={{
          height: virtualizer.getTotalSize(),
          width: "100%",
          position: "relative",
        }}
      >
        {virtualizer.getVirtualItems().map((virtualItem) => {
          if (virtualItem.index === pages.length) {
            return (
              <div
                key="sentinel"
                data-index={virtualItem.index}
                style={{
                  position: "absolute",
                  top: virtualItem.start,
                  left: 0,
                  width: "100%",
                  height: virtualItem.size,
                }}
              >
                <div className="flex items-center gap-2 px-3 py-2 text-xs text-slate-500">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Loading more...
                </div>
              </div>
            );
          }
          const p = pages[virtualItem.index];
          const status = statusByPage[p.id];
          const running =
            runningScopeKeys.has(`page:${p.id}`) ||
            optimisticRunningKeys.has(`page:${p.id}`) ||
            !!livePages[p.id];
          return (
            <div
              key={p.id}
              data-index={virtualItem.index}
              style={{
                position: "absolute",
                top: virtualItem.start,
                left: 0,
                width: "100%",
                height: virtualItem.size,
              }}
              className="pb-2"
            >
              <Row
                label={p.title}
                selected={selectedPageId === p.id}
                onClick={() =>
                  onPageSelected({
                    ...p,
                    siteId,
                    notebookId: notebook.id,
                    notebookName: notebook.displayName,
                    sectionId: section.id,
                    sectionName: section.displayName,
                  })
                }
                badge={<PageBadge page={p} status={status} live={running} />}
                actions={
                  <ScopeActions
                    compact
                    scope={{
                      type: "page",
                      id: p.id,
                      siteId,
                      notebookId: notebook.id,
                      notebookName: notebook.displayName,
                      sectionId: section.id,
                      sectionName: section.displayName,
                      label: p.title,
                    }}
                    autoRule={undefined}
                    onSync={() =>
                      onStartSync({
                        type: "page",
                        id: p.id,
                        siteId,
                        notebookId: notebook.id,
                        notebookName: notebook.displayName,
                        sectionId: section.id,
                        sectionName: section.displayName,
                        label: p.title,
                      })
                    }
                    onSetAuto={() => undefined}
                    running={running}
                  />
                }
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PageBadge({
  page,
  status,
  live,
}: {
  page: Page;
  status?: PageStatus;
  live?: boolean;
}) {
  if (live) {
    return (
      <span
        className="flex items-center gap-1 text-xs text-blue-700"
        title="Currently syncing"
      >
        <Loader2 className="h-3 w-3 animate-spin" />
        Syncing
      </span>
    );
  }
  if (!status) {
    return (
      <span
        className="flex items-center gap-1 text-xs text-slate-400"
        title="Not yet synced"
      >
        <Circle className="h-3 w-3" />
        Not synced
      </span>
    );
  }
  if (status.status === "error") {
    return (
      <span
        className="flex items-center gap-1 text-xs text-red-600"
        title="Sync failed — try re-syncing"
      >
        <XCircle className="h-3 w-3" />
        Error
      </span>
    );
  }
  if (status.status === "syncing") {
    return (
      <span
        className="flex items-center gap-1 text-xs text-slate-500"
        title="Sync started but did not complete — try re-syncing"
      >
        <AlertCircle className="h-3 w-3" />
        Incomplete
      </span>
    );
  }
  if (status.status === "partial") {
    return (
      <span
        className="flex items-center gap-1 text-xs text-amber-700"
        title="Some attachments failed — click to see details"
      >
        <AlertCircle className="h-3 w-3" />
        Partial
      </span>
    );
  }
  const stale =
    status.isStale ||
    (page.lastModifiedDateTime &&
      page.lastModifiedDateTime > status.graphModifiedAt);
  if (stale) {
    return (
      <span
        className="flex items-center gap-1 text-xs text-amber-700"
        title={`Synced ${status.syncedAt}; remote changed since`}
      >
        <AlertCircle className="h-3 w-3" />
        Stale
      </span>
    );
  }
  return (
    <span
      className="flex items-center gap-1 text-xs text-emerald-700"
      title={`Synced ${status.syncedAt}`}
    >
      <CheckCircle2 className="h-3 w-3" />
      {relTime(status.syncedAt)}
    </span>
  );
}

function AutoBadge({ rule }: { rule: AutoSyncRule }) {
  return (
    <span
      className="flex items-center gap-1 rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700"
      title={`Auto-sync every ${rule.intervalMinutes} min${rule.lastRunAt ? `; last ran ${rule.lastRunAt}` : ""}`}
    >
      <Repeat className="h-2.5 w-2.5" />
      {rule.intervalMinutes}m
    </span>
  );
}

function ScopeActions({
  scope,
  autoRule,
  onSync,
  onSetAuto,
  running,
  compact,
}: {
  scope: SyncScope;
  autoRule?: AutoSyncRule;
  onSync: () => void;
  onSetAuto: (rule: { enabled: boolean; intervalMinutes: number }) => void;
  running?: boolean;
  compact?: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Button
        size="sm"
        variant="outline"
        className="h-7 px-2 text-xs"
        onClick={(e) => {
          e.stopPropagation();
          onSync();
        }}
        disabled={running}
        title={running ? "Sync already running" : "Sync this scope from OneNote"}
      >
        {running ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <RefreshCw className="h-3 w-3" />
        )}
        <span className="ml-1">Sync</span>
      </Button>

      {!compact && scope.type !== "page" && (
        <AutoSyncControl rule={autoRule} onSetAuto={onSetAuto} />
      )}
    </div>
  );
}

function AutoSyncControl({
  rule,
  onSetAuto,
}: {
  rule?: AutoSyncRule;
  onSetAuto: (r: { enabled: boolean; intervalMinutes: number }) => void;
}) {
  const enabled = rule?.enabled ?? false;
  const interval = rule?.intervalMinutes ?? 60;
  return (
    <div className="ml-auto flex items-center gap-1 text-xs text-slate-600">
      <label className="flex items-center gap-1">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => onSetAuto({ enabled: e.target.checked, intervalMinutes: interval })}
        />
        Auto
      </label>
      <select
        value={interval}
        disabled={!enabled}
        onChange={(e) =>
          onSetAuto({ enabled, intervalMinutes: Number(e.target.value) })
        }
        className="rounded border border-slate-200 bg-white px-1 py-0.5 text-xs disabled:opacity-50"
      >
        {INTERVAL_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function LoadingRow() {
  return (
    <div className="flex items-center gap-2 px-3 py-2 text-sm text-slate-500">
      <Loader2 className="h-3 w-3 animate-spin" />
      Loading...
    </div>
  );
}

function ErrorRow({ message }: { message: string }) {
  return (
    <div className="px-3 py-2 text-sm text-red-600" title={message}>
      Error: {message.slice(0, 60)}
    </div>
  );
}

function Hint({ children }: { children: React.ReactNode }) {
  return <div className="px-3 py-4 text-xs italic text-slate-400">{children}</div>;
}

// If a worker has touched the row at least once but hasn't ticked for
// STALE_AFTER_MS, assume the worker is stuck (crashed, DLQ'd, IAM, etc.).
// Pure "row created but no worker ack" doesn't count as stuck — the
// message is just queued behind a backlog (common when a page-scope sync
// is enqueued during an active section sync). For those, lastUpdatedAt
// equals startedAt because nothing has updated the row yet.
const STALE_AFTER_MS = 3 * 60 * 1000;

function isStuck(data: { startedAt: string; lastUpdatedAt: string; pagesDone: number; pagesErrored: number }) {
  const lastTick = new Date(data.lastUpdatedAt || data.startedAt).getTime();
  const startedAt = new Date(data.startedAt).getTime();
  // A worker has acked iff lastTick > startedAt (the row got an UpdateItem
  // beyond its initial PutItem). If those are equal, the worker simply
  // hasn't picked up the message yet — that's "queued", not "stuck".
  const workerHasAcked = lastTick > startedAt;
  return (
    workerHasAcked &&
    data.pagesDone + data.pagesErrored === 0 &&
    Date.now() - lastTick > STALE_AFTER_MS
  );
}

function ScopeSyncProgress({ progress }: { progress: SyncProgressData }) {
  const { pagesDone, pagesErrored, pagesTotal, status, phase, purgeTotal, purgeDone } = progress;
  const isPurging = phase === "purging";
  const pct = isPurging
    ? purgeTotal > 0 ? Math.round((purgeDone / purgeTotal) * 100) : 0
    : pagesTotal > 0 ? Math.round(((pagesDone + pagesErrored) / pagesTotal) * 100) : 0;
  // Show synced and failed counts separately — merging them ("X of N pages")
  // hides bursty error spikes (e.g. 320 errored looked the same as 320 done
  // until you noticed the page totals didn't match).
  const errorSuffix = pagesErrored > 0 ? ` · ${pagesErrored} failed` : "";
  const text = isPurging
    ? `Purging ${purgeDone} of ${purgeTotal}`
    : status === "rate_limited"
      ? `Paused — ${pagesDone} of ${pagesTotal}${errorSuffix}`
      : `${pagesDone} of ${pagesTotal} synced${errorSuffix}`;
  const barColor =
    isPurging ? "bg-rose-500" : status === "rate_limited" ? "bg-amber-500" : "bg-sky-500";
  const trackColor =
    isPurging ? "bg-rose-100" : status === "rate_limited" ? "bg-amber-100" : "bg-sky-100";
  const textColor =
    isPurging ? "text-rose-700" : status === "rate_limited" ? "text-amber-700" : "text-sky-700";
  return (
    <div className="mt-1.5 text-[11px]">
      <div className="flex items-center justify-between gap-2">
        <span className={cn("flex items-center gap-1", textColor)}>
          <Loader2 className="h-2.5 w-2.5 animate-spin" />
          {text}
        </span>
        <span className="text-slate-400 tabular-nums">{pct}%</span>
      </div>
      <div className={cn("mt-0.5 h-1 w-full overflow-hidden rounded", trackColor)}>
        <div
          className={cn("h-full transition-all", barColor)}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function SyncProgressBanner({
  jobId,
  onDone,
}: {
  jobId: string;
  onDone: (jobId: string) => void;
}) {
  // Initial fetch: seeds state on mount and when the user reloads the tab
  // mid-sync. After that, live updates flow over the shared WebSocket
  // ("sync_progress" frames) and we patch the cache in-place — no polling.
  // Refetch every 30s as a defensive backstop in case a WS message is
  // lost (network flap, worker restarting, etc.); most updates arrive
  // through WS before that timer fires.
  const q = useQuery({
    queryKey: ["jobProgress", jobId],
    queryFn: () => api.jobProgress(jobId),
    refetchInterval: (query) => {
      const d = query.state.data;
      if (!d) return 2000;
      if (d.status === "completed" || d.status === "failed") return false;
      if (isStuck(d)) return false;
      return 30000;
    },
  });

  // Live updates pushed by sync-worker / dlq-reaper / enqueue API.
  const queryClient = useQueryClient();
  useWsFrames((frame) => {
    if (frame.kind !== "sync_progress") return;
    if (frame.jobId !== jobId) return;
    const progress = frame.progress as SyncProgressData | undefined;
    if (!progress) return;
    queryClient.setQueryData(["jobProgress", jobId], progress);
  });

  useEffect(() => {
    const d = q.data;
    if (!d) return;
    if (d.status === "completed" || d.status === "failed") {
      const t = setTimeout(() => onDone(jobId), 3000);
      return () => clearTimeout(t);
    }
  }, [q.data, jobId, onDone]);

  // Detect "waiting" state: the row exists, no worker has touched it yet,
  // pagesDone+pagesErrored===0. Implies the message is sitting in the SQS
  // queue behind a backlog. Fetch queue depth so the user sees a reason
  // for the wait.
  const isWaitingForWorker = !!q.data
    && q.data.pagesDone + q.data.pagesErrored === 0
    && new Date(q.data.lastUpdatedAt || q.data.startedAt).getTime() <= new Date(q.data.startedAt).getTime();

  const queueDepthQ = useQuery({
    queryKey: ["syncQueueDepth"],
    queryFn: api.syncQueueDepth,
    enabled: isWaitingForWorker,
    refetchInterval: isWaitingForWorker ? 10_000 : false,
  });

  if (!q.data) return null;
  const { pagesDone, pagesErrored, pagesTotal, status, scopeLabel, scopeType, phase, purgeTotal, purgeDone } = q.data;
  const stuck = isStuck(q.data);
  const isPurging = phase === "purging";
  const pct = isPurging
    ? purgeTotal > 0 ? Math.round((purgeDone / purgeTotal) * 100) : 0
    : pagesTotal > 0 ? Math.round(((pagesDone + pagesErrored) / pagesTotal) * 100) : 0;

  const effectiveStatus: typeof status | "stuck" | "waiting" =
    stuck ? "stuck" : isWaitingForWorker ? "waiting" : status;

  const bg =
    effectiveStatus === "failed" || effectiveStatus === "stuck"
      ? "border-red-200 bg-red-50 text-red-800"
      : effectiveStatus === "partial"
        ? "border-amber-300 bg-amber-50 text-amber-900"
        : effectiveStatus === "completed"
          ? "border-emerald-200 bg-emerald-50 text-emerald-800"
          : effectiveStatus === "rate_limited"
            ? "border-amber-200 bg-amber-50 text-amber-800"
            : effectiveStatus === "waiting"
              ? "border-slate-200 bg-slate-50 text-slate-700"
              : isPurging
                ? "border-rose-200 bg-rose-50 text-rose-800"
                : "border-sky-200 bg-sky-50 text-sky-800";

  const label = scopeLabel ?? `${scopeType} sync`;

  return (
    <div className={cn("rounded-md border p-2 text-xs", bg)}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {effectiveStatus === "completed" ? (
            <CheckCircle2 className="h-4 w-4" />
          ) : effectiveStatus === "partial" ? (
            <AlertTriangle className="h-4 w-4" />
          ) : effectiveStatus === "failed" || effectiveStatus === "stuck" ? (
            <XCircle className="h-4 w-4" />
          ) : effectiveStatus === "rate_limited" ? (
            <Clock className="h-4 w-4" />
          ) : effectiveStatus === "waiting" ? (
            <Clock className="h-4 w-4" />
          ) : (
            <Loader2 className="h-4 w-4 animate-spin" />
          )}
          <span className="font-medium">{label}</span>
          <span className="text-slate-600">
            {effectiveStatus === "completed"
              ? `Synced ${pagesDone} of ${pagesTotal}${pagesErrored ? ` (${pagesErrored} failed)` : ""}`
              : effectiveStatus === "partial"
                ? `Synced ${pagesDone} of ${pagesTotal} — ${pagesErrored} failed (click for trace IDs)`
                : effectiveStatus === "failed"
                  ? `Failed — ${pagesErrored} of ${pagesTotal} errored`
                  : effectiveStatus === "stuck"
                    ? `Stuck — no progress in 3 min (worker may have crashed)`
                    : effectiveStatus === "rate_limited"
                      ? `Paused — Microsoft is throttling. ${pagesDone} of ${pagesTotal} done, auto-resuming shortly.`
                      : effectiveStatus === "waiting"
                        ? (() => {
                            const d = queueDepthQ.data;
                            const ahead = d ? d.rawFetch.queued + d.rawFetch.inFlight : null;
                            return ahead != null
                              ? `Waiting in queue — ~${ahead} message${ahead === 1 ? "" : "s"} ahead`
                              : `Waiting in queue — picking up shortly…`;
                          })()
                        : isPurging
                          ? `Deleting old data — ${purgeDone} of ${purgeTotal} pages`
                          : pagesErrored > 0
                            ? `${pagesDone} of ${pagesTotal} synced · ${pagesErrored} failed`
                            : `${pagesDone} of ${pagesTotal} pages`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-slate-500 tabular-nums">{pct}%</span>
          {(effectiveStatus === "failed" || effectiveStatus === "partial" || effectiveStatus === "stuck" || effectiveStatus === "waiting") && (
            <button
              type="button"
              onClick={() => onDone(jobId)}
              className="rounded px-1 text-slate-500 hover:bg-slate-100"
              title="Dismiss"
            >
              ×
            </button>
          )}
        </div>
      </div>
      {effectiveStatus === "queued" ||
      effectiveStatus === "running" ||
      effectiveStatus === "rate_limited" ? (
        <div
          className={cn(
            "mt-1 h-1 w-full overflow-hidden rounded",
            effectiveStatus === "rate_limited"
              ? "bg-amber-100"
              : isPurging
                ? "bg-rose-100"
                : "bg-sky-100",
          )}
        >
          <div
            className={cn(
              "h-full transition-all",
              effectiveStatus === "rate_limited"
                ? "bg-amber-500"
                : isPurging
                  ? "bg-rose-500"
                  : "bg-sky-500",
            )}
            style={{ width: `${pct}%` }}
          />
        </div>
      ) : null}
      {(effectiveStatus === "failed" || effectiveStatus === "partial" || effectiveStatus === "stuck") && q.data.lastError ? (
        <div className={cn("mt-1 truncate text-xs", effectiveStatus === "partial" ? "text-amber-800" : "text-red-700")} title={q.data.lastError}>
          {q.data.lastError}
        </div>
      ) : null}
      {pagesErrored > 0 && (
        <JobErrorsPanel jobId={jobId} pagesErrored={pagesErrored} />
      )}
    </div>
  );
}

// JobErrorsPanel — sticky list of errored pages for one job. Sits inside
// the SyncProgressBanner when pagesErrored > 0. Each row shows the page
// title, friendly classified cause, copyable 8-char trace ID + Microsoft
// request-id, and a retry button. Persistence via localStorage so the
// user can × dismiss and not have it pop back on refresh.
function JobErrorsPanel({
  jobId,
  pagesErrored,
}: {
  jobId: string;
  pagesErrored: number;
}) {
  const dismissKey = `sync_errors_dismissed_${jobId}`;
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(dismissKey) === "1";
    } catch {
      return false;
    }
  });
  const [expanded, setExpanded] = useState<boolean>(true);
  const [retryingPageId, setRetryingPageId] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const errorsQ = useQuery({
    queryKey: ["jobErrors", jobId],
    queryFn: () => api.jobErrors(jobId),
    enabled: !dismissed,
    refetchInterval: dismissed ? false : 30_000,
  });

  const retryMut = useMutation({
    mutationFn: (page: {
      pageId: string;
      title: string;
      siteId: string;
      notebookId: string;
      notebookName: string;
      sectionId: string;
      sectionName: string;
    }) =>
      api.sync({
        type: "page",
        id: page.pageId,
        siteId: page.siteId,
        notebookId: page.notebookId,
        notebookName: page.notebookName,
        sectionId: page.sectionId,
        sectionName: page.sectionName,
        label: page.title,
      }),
    onError: (err) => {
      console.error("retry sync failed:", err);
    },
  });

  const dismiss = () => {
    try { localStorage.setItem(dismissKey, "1"); } catch { /* ignore quota errors */ }
    setDismissed(true);
  };

  const copy = (label: string, value: string) => {
    void navigator.clipboard.writeText(value);
    setCopiedField(label);
    setTimeout(() => setCopiedField(null), 1500);
  };

  if (dismissed) return null;
  const data = errorsQ.data;
  if (!data || data.errors.length === 0) return null;

  return (
    <div className="mt-2 rounded border border-amber-300 bg-white text-[11px]">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-2 px-2 py-1.5 text-left font-medium text-amber-900 hover:bg-amber-50"
      >
        {expanded ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
        <AlertTriangle className="h-3 w-3 text-amber-700" />
        <span>
          Errors ({pagesErrored})
        </span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            dismiss();
          }}
          className="ml-auto rounded px-1 text-amber-500 hover:bg-amber-100 hover:text-amber-800"
          title="Dismiss until next sync"
        >
          ×
        </button>
      </button>
      {expanded && (
        <div className="border-t border-amber-200 px-2 py-1.5">
          {data.tip && (
            <div className="mb-2 rounded bg-amber-50 px-2 py-1 text-amber-800">
              <span className="font-medium">Tip: </span>
              {data.tip}
            </div>
          )}
          <ul className="space-y-1.5">
            {data.errors.map((e) => {
              const retrying = retryingPageId === e.pageId && retryMut.isPending;
              return (
                <li
                  key={e.pageId}
                  className="flex flex-col gap-1 rounded border border-slate-200 bg-slate-50 px-2 py-1.5"
                >
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium text-slate-900">{e.title}</div>
                      <div className="text-slate-700">{e.errorMessage ?? "Unknown error"}</div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      {e.webUrl && (
                        <a
                          href={e.webUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex h-6 w-6 items-center justify-center rounded text-slate-500 hover:bg-slate-200"
                          title="Open in OneNote"
                        >
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          setRetryingPageId(e.pageId);
                          retryMut.mutate({
                            pageId: e.pageId,
                            title: e.title,
                            siteId: e.siteId,
                            notebookId: e.notebookId,
                            notebookName: e.notebookName,
                            sectionId: e.sectionId,
                            sectionName: e.sectionName,
                          });
                        }}
                        disabled={retrying}
                        className="inline-flex h-6 items-center gap-1 rounded border border-slate-300 bg-white px-1.5 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                        title="Re-sync this page"
                      >
                        {retrying ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <RefreshCw className="h-3 w-3" />
                        )}
                        <span>Retry</span>
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-slate-500">
                    {e.errorId && (
                      <button
                        type="button"
                        onClick={() => copy(`trace_${e.errorId}`, e.errorId!)}
                        className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-1 py-0.5 hover:bg-slate-50"
                        title="Copy trace ID for support"
                      >
                        <span className="text-slate-500">trace</span>
                        <code className="font-mono">{e.errorId}</code>
                        {copiedField === `trace_${e.errorId}` ? (
                          <Check className="h-2.5 w-2.5 text-emerald-600" />
                        ) : (
                          <Copy className="h-2.5 w-2.5" />
                        )}
                      </button>
                    )}
                    {e.errorProviderTraceId && (
                      <button
                        type="button"
                        onClick={() => copy(`upstream_${e.pageId}`, e.errorProviderTraceId!)}
                        className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-1 py-0.5 hover:bg-slate-50"
                        title="Copy Microsoft / OpenAI request-id"
                      >
                        <span className="text-slate-500">upstream</span>
                        <code className="font-mono">{e.errorProviderTraceId.slice(0, 12)}…</code>
                        {copiedField === `upstream_${e.pageId}` ? (
                          <Check className="h-2.5 w-2.5 text-emerald-600" />
                        ) : (
                          <Copy className="h-2.5 w-2.5" />
                        )}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function relTime(iso: string): string {
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}


